import { useState } from 'react';
import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Button, Input, Typography } from '../ui';
import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { GroupActionError, useWhatsappGroupActions } from '../../settings/shared/settings-queries';
import { MAX_GROUP_PARTICIPANTS, WHATSAPP_PAGE } from './constants';
import styles from './GroupFormPanel.module.css';

// The DS modal behind both "New Group" and "Edit Name" (Figma 12815-181203): the title in the modal
// header, group name + optional description (over the participants hint) in the body, and Close +
// Create/Save in the footer. Create takes both fields; edit renames the group, which is the only field
// the server lets an owner change after the group exists.
//
// The fields are DS `Input`s carrying their own label and bottom message — no hand-rolled `<label>`s,
// which is what put 16px semibold labels and a detached hint paragraph on a design that asks for the
// DS's 14px medium label and the hint inside the description field's message slot.
export function GroupFormPanel({ group, onDone }: { group?: WhatsappGroup; onDone: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { create, rename } = useWhatsappGroupActions();
  const editing = !!group;
  const [subject, setSubject] = useState(group?.subject ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = subject.trim();
  const canSubmit = trimmed.length > 0 && !busy && (!editing || trimmed !== group.subject.trim());

  const submit = async () => {
    if (!canSubmit) return;
    logPageClickBi({ ...WHATSAPP_PAGE, button_name: editing ? 'rename_group' : 'create_group' });
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await rename(group.id, trimmed);
      } else {
        await create({ subject: trimmed, description });
      }
      onDone();
    } catch (err) {
      const fallback = editing ? 'settings.whatsapp.group.rename.error' : 'settings.whatsapp.newGroup.error';
      setError(err instanceof GroupActionError && err.guard ? err.guard : t(fallback));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onDone}
      size="minimum"
      labelledBy="wa-group-form-title"
      maxHeightPx={650}
      scrollBody
      title={t(editing ? 'settings.whatsapp.group.editName' : 'settings.whatsapp.newGroup.title')}
      headerSeparator
      footerSeparator
      footer={
        <>
          <Button variant="outlined" onClick={onDone}>
            {t('common.close')}
          </Button>
          <Button color="standard" onClick={submit} disabled={!canSubmit} loading={busy}>
            {t(editing ? 'common.save' : 'settings.whatsapp.newGroup.create')}
          </Button>
        </>
      }
    >
      <div className={styles.fields}>
        <Input
          label={t('settings.whatsapp.newGroup.subject')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={80}
          autoFocus
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />

        {!editing && (
          <Input
            label={t('settings.whatsapp.newGroup.description')}
            statusMessage={t('settings.whatsapp.newGroup.participantsHint', { count: MAX_GROUP_PARTICIPANTS })}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={300}
            disabled={busy}
          />
        )}

        {error && (
          <Typography className={styles.error} variant="p2" role="alert">
            {error}
          </Typography>
        )}
      </div>
    </Modal>
  );
}
